import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom"
import { TopBar } from "../../../widgets";
import dayjs from "dayjs";
import KakaoMap from "../../../components/map/kakao";
import { useEffect, useRef, useState } from "react";
import { Carousel, DividerThin } from "../../../shared";
import { ReactComponent as ScheduleV2 } from "../../../assets/images/icon/scheduleV2.svg";
import { ReactComponent as LocationV3 } from "../../../assets/images/icon/locationV3.svg";
import { ReactComponent as ArrowDown } from "../../../assets/images/icon/arrow-down.svg";
import { ReactComponent as ArrowUp } from "../../../assets/images/icon/arrow-up.svg";
import { useGetProgramById } from "../book/firstStep/model";
import { Flex } from "antd";
import WellnessLecutreDetailSkeleton from "../../../components/skeleton/lecture/lectureDetailSkeleton";
import { isLogin } from "../../../utils";
import { setUrl } from "../../../service";
import './index.css';
import { isIOS } from "react-device-detect";
import TagHead from "../../../components/tag/head";
import { selectProgramRequest, shareProgram, viewProgram } from "../../../utils/gtm";

const WellnessProgramDetail = () => {
    const navigate = useNavigate();
    const id = Number(useParams().id);
    const [isDetailOpen, setIsDetailOpen] = useState<boolean>(false);
    const topBarRef = useRef<HTMLDivElement>(null);
    const detailRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState('0px');
    const [disableBtn, setDisableBtn] = useState<boolean>(true);
    const [btnText, setBtnText] = useState<string>();
    const responseQuery = "detailImageUrlList title isPaid description isDisableBooking thumbnailImageUrl bookingPeriod programPeriod detailHtml isPaid isOnline roadNameAddress lat lon locationName notificationHtml refundNotiHtml id programTicketList { id name description payAmount } programEventList { targetDateTime isActive isMaxBooking maxBookingCnt bookingCnt } programEventOutlineList { startTime day } myProgramBookingList { status bookingDateTime }"
    const { data, isPending, isError } = useGetProgramById(id, responseQuery)
    const sentPercents = useRef<Set<number>>(new Set())
    const location = useLocation()

    const onClickBookingBtn = () => {
        if (!data || disableBtn) return;
        selectProgramRequest(data.title, data.isPaid ? '유료' : '무료', data.isOnline ? '온라인' : '오프라인')
        if (!isLogin()) {
            setUrl(window.location.pathname); navigate('/login');
        } else {
            navigate(`/wellness-program/book/first-step/${data.id}`)
        }
    }

    const handleDisableBtn = (bookingPeriod: string[], programPeriod: string[]) => {
        const [startBookingDate, endBookingDate] = bookingPeriod;
        const [startProgramDate, endProgramDate] = programPeriod;

        const endBooking = dayjs().isAfter(dayjs(endBookingDate))
        const notStartBooking = dayjs().isBefore(dayjs(startBookingDate))
        const endProgram = dayjs().isAfter(dayjs(endProgramDate).endOf('d'))

        if (endProgram) {
            setBtnText('프로그램 종료')
        } else if (notStartBooking) {
            setBtnText('오픈 예정')
        } else if (endBooking || data?.isDisableBooking) {
            setBtnText('예약마감')
        } else {
            setBtnText('신청하기')
        }

        return endBooking || (data?.isDisableBooking ?? false) || notStartBooking || endProgram;
    }
    const handleShareClick = () => {
        if (!data) return
        const isValidUrl = /^\/wellness-program\/\d+$/.test(location.pathname)
        if (!isValidUrl) return
        shareProgram(data.title, data?.isPaid ? '유료' : '무료', data.isOnline ? '온라인' : '오프라인')
    }

    useEffect(() => {
        if (isDetailOpen && detailRef.current) {
            setHeight(`${detailRef.current.scrollHeight}px`);
        } else {
            setHeight('686px');
        }
    }, [isDetailOpen]);

    useEffect(() => {
        if (!data) return
        setDisableBtn(handleDisableBtn(data.bookingPeriod, data.programPeriod))
    }, [data])

    useEffect(() => {
        if (!data) return
        viewProgram(data.title, '0%')

        const handleScroll = () => {
            const scrollTop = window.scrollY
            const windowHeight = window.innerHeight
            const docHeight = document.documentElement.scrollHeight
            const totalScrollable = docHeight - windowHeight

            if (totalScrollable <= 0) return

            const scrolledPercent = Math.floor((scrollTop / totalScrollable) * 100)

            const thresholds: [25, 50, 75] = [25, 50, 75]
            thresholds.forEach(threshold => {
                if (scrolledPercent >= threshold && !sentPercents.current.has(threshold)) {
                    sentPercents.current.add(threshold)
                    viewProgram(data.title, `${threshold}%`)
                }
            })
        }

        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [data])

    if (isNaN(id)) return <Navigate to={"/explore"} replace />
    if (data === undefined || isPending) return <WellnessLecutreDetailSkeleton />

    return <>
        <TagHead title={data.title} imgUrl={data.thumbnailImageUrl} description={data.description} />
        <TopBar position='fixed' title="프로그램 상세" rightIcon rightType='share' thema='dark' customShareData={{ title: data.title, text: data.description, imgUrl: data.thumbnailImageUrl }} addHandleShareClick={handleShareClick} />
        <Carousel carouselList={data.detailImageUrlList?.map((q: string) => ({ imageUrl: q, linkUrl: '' }))} imgSize={{ x: 1, y: 1 }} />
        <div className="wellness-program-detail-container">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '24px 20px 32px' }}>
                <div className="sub-title/st4">
                    {data.title}
                </div>
                {data.isPaid &&
                    <div className="sub-title/st3 base/black">
                        {data.programTicketList?.sort((a, b) => a.payAmount - b.payAmount)[0].payAmount.toLocaleString()}원 -
                    </div>}
                {data.myProgramBooking && <div className="body/b2 secondary/s600">
                    {data.myProgramBooking.status === 'BOOKING'
                        ? '예약 대기중입니다'
                        : data.myProgramBooking.status === 'CANCEL'
                            ? '에약 취소 되었습니다.'
                            : data.myProgramBooking.status === 'COMPLETE'
                                ? '에약 확정되었습니다.'
                                : data.myProgramBooking.status === 'REFUND'
                                    ? '환불 완료되었습니다.'
                                    : ''}
                </div>}
                <div className="body/b2 secondary/s600">
                    {data.description}
                </div>
            </div>

            <div style={{ padding: '0 20px 16px' }}>
                <DividerThin style={{ marginBottom: 16 }} />
                <div style={{ display: 'flex', gap: '16px', paddingInline: '12px' }}>
                    <ScheduleV2 width={24} height={24} stroke="var(--secondarys400)" fill="var(--secondarys400)" />
                    <div>
                        <div className="sub-title/st3 secondary/s850" style={{ marginBottom: '2px' }}>
                            {data.programPeriod && dayjs(data.programPeriod[0]).format('YYYY.MM.DD') + ' - ' + dayjs(data.programPeriod[1]).format('YYYY.MM.DD')}
                        </div>
                        <div className="body/b2 secondary/s600">
                            {Array.from(new Set(data.programEventOutlineList.flatMap(i => i.day)))
                                .sort((a, b) => a - b)
                                .map((day, idx) => <div key={idx}>
                                    <span style={{ marginRight: 5 }}>{['월', '화', '수', '목', '금', '토', '일'][day - 1]}</span>
                                    <span>{data.programEventOutlineList
                                        .filter(eventOutline => eventOutline.day.includes(day))
                                        .map(eventOutline => eventOutline.startTime)
                                        .join(', ')
                                    }</span>
                                </div>)}
                        </div>
                    </div>
                </div>
                {data.isOnline === false &&
                    <div style={{ display: 'flex', gap: '16px', paddingInline: '12px', paddingTop: '20px' }}>
                        <LocationV3 width={24} height={24} stroke="var(--secondarys400)" fill="white" />
                        <div>
                            <div className="sub-title/st3 secondary/s850" style={{ marginBottom: '2px' }}>
                                {data.locationName ?? ''}
                            </div>
                            <div className="body/b2 secondary/s600">
                                {data.roadNameAddress ?? ''}
                            </div>
                        </div>
                    </div>}
                <DividerThin style={{ marginTop: 16 }} />
            </div>
            <div style={{ padding: '16px 20px' }}>
                <div className="sub-title/st3 base/black" style={{ marginBottom: '16px' }}>
                    프로그램 상세
                </div>
                <div>
                    <div ref={detailRef} className="body/b3 secondary/s850" style={{ transition: "max-height 0.3s ease", maxHeight: isDetailOpen ? height : '500px', overflow: 'hidden', whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: data.detailHtml }} />
                    {(detailRef.current?.offsetHeight ?? 0) >= 500 &&
                        <div style={{ textAlign: 'center', marginTop: '16px' }} onClick={() => setIsDetailOpen(!isDetailOpen)}>
                            <div className="caption/c1 secondary/s600" style={{ textAlign: 'center', cursor: 'pointer' }} >
                                {isDetailOpen ? '접기' : '더보기'}
                            </div>
                            {isDetailOpen
                                ? <ArrowUp width={16} height={16} stroke="var(--secondarys400)" />
                                : <ArrowDown width={16} height={16} stroke="var(--secondarys400)" />}
                        </div>}
                </div>
            </div>

            {data.isPaid === true &&
                <div style={{ padding: '16px 20px' }}>
                    <div className="sub-title/st3 base/black" style={{ marginBottom: 16 }}>티켓</div>
                    {data.programTicketList?.sort((a, b) => a.id - b.id).map((ticket, idx, arr) =>
                        <Flex key={ticket.id} justify="space-between" style={{ padding: '12px 0', marginBlock: '4px', borderBottom: idx === (arr.length - 1) ? 'none' : '1px solid var(--secondarys100)', alignItems: 'center' }} gap={16}>
                            <div>
                                <div className="sub-title/st3 secondary/s850">{ticket.name}</div>
                                <div className="body/b2 secondary/s600" style={{ marginTop: 5 }}>{ticket.description}</div>
                            </div>
                            <div className="sub-title/st3" style={{ minWidth: 100, textAlign: 'right' }}>{ticket.payAmount.toLocaleString()}원</div>
                        </Flex>)}
                </div>}
            {data.isOnline === false &&
                <div style={{ padding: '16px 20px' }}>
                    <div className="sub-title/st3 base/black" style={{ marginBottom: '16px' }}>
                        위치
                    </div>
                    <div className="body/b3 secondary/s850" style={{ marginBottom: '16px' }}>
                        {(data.roadNameAddress ? data.roadNameAddress + ', ' : '') + (data.locationName ?? '')}
                    </div>

                    <KakaoMap lat={data.lat ?? 127.035349} lon={data.lon ?? 37.522466} style={{ borderRadius: 'var(--radius4)' }} />
                </div>}
            <div style={{ padding: '16px 20px' }}>
                <div className="sub-title/st3 base/black" style={{ marginBottom: '16px' }}>
                    안내사항
                </div>
                <div className="body/b3 secondary/s800" style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: data.notificationHtml }} />
            </div>
            <div style={{ padding: '16px 20px 40px' }}>
                <div className="sub-title/st3 base/black" style={{ marginBottom: '16px' }}>
                    취소・환불 규정
                </div>
                <div className="body/b3 secondary/s800" style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: data.refundNotiHtml }} />
            </div>

            {btnText &&
                <div key={data?.id} style={{ position: 'sticky', bottom: 0, textAlign: 'center', width: '100%', maxWidth: '480px', zIndex: 1, cursor: disableBtn ? 'no-drop' : 'pointer' }} onClick={onClickBookingBtn}>
                    <div className={"sub-title/st3 " + (disableBtn ? "secondary/s400" : "base/white")} style={{ backgroundColor: disableBtn ? 'var(--secondarys200)' : 'black', paddingBlock: 20, marginInline: '20px', borderRadius: 'var(--radius4)' }}>
                        {btnText}
                    </div>
                    <div style={{ height: isIOS ? '34px' : '20px' }} />
                </div>}
        </div>
    </>
}

export default WellnessProgramDetail
